import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Platform, Modal, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { talentTeachersApi, talentTeacherProjectLinksApi, talentSpecializationsApi, talentProjectsApi, talentActivitiesApi, talentStudentsApi } from '../../../services/apiService';
import { Teacher } from '../types';
import { MainStackParamList } from '../../../navigation/MainNavigator';

interface DetailTeachersTableProps {
  specializationId?: number;
  projectId?: number;
  activityId?: number;
  entityName?: string;
  readOnly?: boolean;
  /** Whether the current user may add/import/remove coordinators. */
  canManage?: boolean;
}

export default function DetailTeachersTable({ specializationId, projectId, activityId, entityName, readOnly, canManage }: DetailTeachersTableProps) {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const navigation = useNavigation<NavigationProp<MainStackParamList>>();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;

  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [deleteTarget, setDeleteTarget] = useState<Teacher | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', school: '', email: '', phone: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);
  const [showAddSchoolDropdown, setShowAddSchoolDropdown] = useState(false);
  const [existingTeachers, setExistingTeachers] = useState<any[]>([]);
  const [selectedExistingId, setSelectedExistingId] = useState<number | null>(null);
  const [existingSearch, setExistingSearch] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [viewTeacher, setViewTeacher] = useState<Teacher | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageSize = 10;

  // Import wizard state
  const [importStep, setImportStep] = useState<'pick' | 'assign' | 'conflicts' | 'resolve'>('pick');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSpecId, setImportSpecId] = useState<number | undefined>(specializationId);
  const [importProjId, setImportProjId] = useState<number | undefined>(undefined);
  const [importActId, setImportActId] = useState<number | undefined>(undefined);
  const [specOptions, setSpecOptions] = useState<{ id: number; name: string }[]>([]);
  const [projOptions, setProjOptions] = useState<{ id: number; name: string }[]>([]);
  const [actOptions, setActOptions] = useState<{ id: number; name: string }[]>([]);
  const [importConflicts, setImportConflicts] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, {
    selectedTeacherId: number | null;
    isNew: boolean;
    editedData: { name: string; eid: string; email: string; phone: string };
    hasEdited: boolean;
  }>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [assignSpecName, setAssignSpecName] = useState('');
  const [assignProjName, setAssignProjName] = useState('');
  const [assignActName, setAssignActName] = useState('');

  // Whether we're inside a project/activity context (assign mode)
  const isAssignMode = !!(projectId || activityId);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 1000 };
      if (specializationId) params.specialization = specializationId;
      if (activityId) params.activity = activityId;
      else if (projectId) params.project = projectId;
      const res = await talentTeachersApi.list(params);
      const results = (res.results || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        specialization: t.specialization_name || '',
        school: t.school || '',
        email: t.email || '',
        phone: t.phone || '',
        isVerified: t.is_verified,
        isActive: t.is_active,
        hasCertificate: t.has_certificate,
        projectNames: t.project_names || [],
      }));
      setTeachers(results);
    } catch (err) {
      console.log('[DetailTeachersTable] API failed:', err);
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  }, [specializationId, projectId, activityId]);

  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);

  useEffect(() => { if (!showAddModal) setShowAddSchoolDropdown(false); }, [showAddModal]);

  // School choices — same list used for students (SCHOOL_CHOICES on the backend)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const schools = await talentStudentsApi.schools();
        if (!cancelled) setSchoolOptions(schools);
      } catch (e) {
        console.log('[DetailTeachersTable] load schools failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const data = useMemo(() => {
    let filtered = [...teachers];
    if (sortOrder) {
      filtered.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name, 'ar');
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    const count = filtered.length;
    const start = (currentPage - 1) * pageSize;
    return { results: filtered.slice(start, start + pageSize), count, page: currentPage, pageSize };
  }, [teachers, currentPage, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(data.count / pageSize));

  const toggleSort = () => {
    setSortOrder((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : 'asc'));
    setCurrentPage(1);
  };

  const handleDeleteTeacher = (teacher: Teacher) => {
    setDeleteTarget(teacher);
  };

  const confirmDeleteTeacher = async () => {
    if (!deleteTarget) return;
    try {
      // In detail views: unassign (remove link), don't delete the record
      if (projectId) {
        // Remove from project (teachers are linked to projects)
        await talentTeachersApi.removeProject(deleteTarget.id, projectId);
      } else if (specializationId) {
        await talentTeachersApi.removeSpecialization(deleteTarget.id);
      }
      setToastMessage(isRTL ? `تم إلغاء إلحاق "${deleteTarget.name}" بنجاح` : `"${deleteTarget.name}" unassigned`);
      setToastType('success');
      setToastVisible(true);
      await fetchTeachers();
    } catch {
      setToastMessage(isRTL ? 'خطأ في إلغاء إلحاق المنسق' : 'Error unassigning teacher');
      setToastType('error');
      setToastVisible(true);
    }
    setDeleteTarget(null);
  };

  // Fetch existing teachers for assign dropdown + add modal
  // Load ALL teachers (no spec/project filter) so we can see and assign any
  useEffect(() => {
    (async () => {
      try {
        const res = await talentTeachersApi.list({ page_size: 1000 });
        const items = (res.results || []).map((t: any) => ({
          id: t.id, name: t.name, email: t.email || '', phone: t.phone || '', school: t.school || '',
        }));
        console.log('[DetailTeachersTable] Loaded existing teachers for dropdown:', items.length);
        setExistingTeachers(items);
      } catch (e) { console.log('[DetailTeachersTable] Failed to load existing teachers:', e); setExistingTeachers([]); }
    })();
  }, []);

  // Reset selection when modal opens
  useEffect(() => {
    if (showAddModal) { setSelectedExistingId(null); setExistingSearch(''); }
  }, [showAddModal]);

  const handleSelectExistingTeacher = (teacher: any) => {
    setSelectedExistingId(teacher.id);
    setAddForm({
      name: teacher.name,
      school: teacher.school, email: teacher.email, phone: teacher.phone,
    });
    setExistingSearch('');
  };

  const handleClearTeacherSelection = () => {
    setSelectedExistingId(null);
    setAddForm({ name: '', school: '', email: '', phone: '' });
  };

  const handleAddTeacher = async () => {
    // Assign existing teacher
    if (selectedExistingId) {
      setAddLoading(true);
      try {
        if (specializationId) {
          await talentTeachersApi.update(selectedExistingId, { specialization: specializationId });
        }
        if (projectId) {
          await talentTeacherProjectLinksApi.create({ teacher: selectedExistingId, project: projectId });
        }
        setToastMessage(isRTL ? 'تم إلحاق المنسق بنجاح' : 'Coordinator assigned successfully');
        setToastType('success');
        setToastVisible(true);
        setShowAddModal(false);
        setSelectedExistingId(null);
        setAddForm({ name: '', school: '', email: '', phone: '' });
        await fetchTeachers();
      } catch (err: any) {
        setToastMessage(isRTL ? 'خطأ في إلحاق المنسق' : 'Error assigning coordinator');
        setToastType('error');
        setToastVisible(true);
      }
      setAddLoading(false);
      return;
    }

    // Create new teacher
    if (!addForm.name.trim()) {
      setToastMessage(isRTL ? 'يرجى إدخال اسم المنسق' : 'Please enter coordinator name');
      setToastType('error');
      setToastVisible(true);
      return;
    }
    setAddLoading(true);
    try {
      const payload: any = {
        name: addForm.name,
        school: addForm.school,
        email: addForm.email,
        phone: addForm.phone,
      };
      if (specializationId) {
        payload.specialization = specializationId;
      }
      const created = await talentTeachersApi.create(payload);
      if (projectId && created?.id) {
        await talentTeacherProjectLinksApi.create({ teacher: created.id, project: projectId });
      }
      setToastMessage(isRTL ? 'تم إضافة المنسق بنجاح' : 'Coordinator added successfully');
      setToastType('success');
      setToastVisible(true);
      setShowAddModal(false);
      setSelectedExistingId(null);
      setAddForm({ name: '', school: '', email: '', phone: '' });
      await fetchTeachers();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || (isRTL ? 'خطأ في إضافة المنسق' : 'Error adding teacher');
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    talentTeachersApi.downloadTemplate();
  };

  const handlePickFile = () => {
    if (Platform.OS === 'web' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetImportWizard = () => {
    setImportStep('pick');
    setImportFile(null);
    setImportConflicts([]);
    setImportResult(null);
    setConflictResolutions({});
    setShowSkipConfirm(false);
    setAssignSpecName('');
    setAssignProjName('');
    setAssignActName('');
  };

  const onFileSelected = async (event: any) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    setImportFile(file);

    // If in assign mode, pre-set IDs from props and resolve names
    if (isAssignMode) {
      setImportSpecId(specializationId);
      setImportProjId(projectId);
      setImportActId(activityId);
      // Fetch spec options & resolve spec name
      try {
        const resp = await talentSpecializationsApi.list({ page_size: 200 });
        const list = resp?.results || resp || [];
        const mapped = list.map((s: any) => ({ id: s.id, name: s.name }));
        setSpecOptions(mapped);
        if (specializationId) {
          const match = mapped.find((s: any) => s.id === Number(specializationId));
          if (match) setAssignSpecName(match.name);
        }
      } catch { /* ignore */ }
      // Fetch project options & resolve project name
      if (specializationId) {
        try {
          const resp = await talentProjectsApi.list({ specialization: specializationId, page_size: 200 });
          const list = resp?.results || resp || [];
          const mapped = list.map((p: any) => ({ id: p.id, name: p.name }));
          setProjOptions(mapped);
          if (projectId) {
            const match = mapped.find((p: any) => p.id === Number(projectId));
            if (match) setAssignProjName(match.name);
          }
        } catch { /* ignore */ }
      }
      // Fetch activity options & resolve activity name
      if (projectId) {
        try {
          const resp = await talentActivitiesApi.list({ project: projectId, page_size: 200 });
          const list = resp?.results || resp || [];
          const mapped = list.map((a: any) => ({ id: a.id, name: a.name }));
          setActOptions(mapped);
          if (activityId) {
            const match = mapped.find((a: any) => a.id === Number(activityId));
            if (match) setAssignActName(match.name);
          }
        } catch { setActOptions([]); }
      }
      setImportStep('assign');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Fetch specialization options
    try {
      const resp = await talentSpecializationsApi.list({ page_size: 200 });
      const list = resp?.results || resp || [];
      const mapped = list.map((s: any) => ({ id: s.id, name: s.name }));
      setSpecOptions(mapped);
      // Pre-select specialization if known from prop
      if (specializationId) {
        setImportSpecId(specializationId);
        const match = mapped.find((s: any) => s.id === Number(specializationId));
        if (match) setAssignSpecName(match.name);
      }
    } catch { setSpecOptions([]); }
    // If specialization is known, pre-fetch projects
    if (specializationId) {
      try {
        const resp = await talentProjectsApi.list({ specialization: specializationId, page_size: 200 });
        const list = resp?.results || resp || [];
        setProjOptions(list.map((p: any) => ({ id: p.id, name: p.name })));
      } catch { setProjOptions([]); }
    }
    setImportStep('assign');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // When import spec changes, fetch projects
  const onImportSpecChange = async (specId: number | undefined) => {
    setImportSpecId(specId);
    setImportProjId(undefined);
    setImportActId(undefined);
    setProjOptions([]);
    setActOptions([]);
    if (!specId) return;
    try {
      const resp = await talentProjectsApi.list({ specialization: specId, page_size: 200 });
      const list = resp?.results || resp || [];
      setProjOptions(list.map((p: any) => ({ id: p.id, name: p.name })));
    } catch { setProjOptions([]); }
  };

  // When import project changes, fetch activities
  const onImportProjChange = async (projId: number | undefined) => {
    setImportProjId(projId);
    setImportActId(undefined);
    setActOptions([]);
    if (!projId) return;
    try {
      const resp = await talentActivitiesApi.list({ project: projId, page_size: 200 });
      const list = resp?.results || resp || [];
      setActOptions(list.map((a: any) => ({ id: a.id, name: a.name })));
    } catch { setActOptions([]); }
  };

  // Final import submit
  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const result = await talentTeachersApi.importExcel(importFile, importSpecId, importProjId, importActId);

      // Check for conflicts
      if (result.conflicts && result.conflicts.length > 0) {
        setImportResult(result);
        setImportConflicts(result.conflicts);
        const resMap: Record<number, any> = {};
        result.conflicts.forEach((c: any) => {
          resMap[c.row] = {
            selectedTeacherId: null,
            isNew: false,
            editedData: { name: c.row_data.name, eid: c.row_data.eid || '', email: c.row_data.email || '', phone: c.row_data.phone || '' },
            hasEdited: false,
          };
        });
        setConflictResolutions(resMap);
        setImportStep('conflicts');
        if (result.created > 0 || result.existing > 0) {
          setToastMessage(result.message);
          setToastType('info');
          setToastVisible(true);
        }
        await fetchTeachers();
      } else {
        // No conflicts
        setToastMessage(result.message || (isRTL ? `تم استيراد ${result.imported} منسق` : `Imported ${result.imported} teachers`));
        setToastType('success');
        setToastVisible(true);
        setShowImportModal(false);
        resetImportWizard();
        await fetchTeachers();
        setCurrentPage(1);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || (isRTL ? 'خطأ في استيراد الملف' : 'Import error');
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setImporting(false);
    }
  };

  // Handle resolving conflicts
  const handleResolveConflicts = async () => {
    // Validate: must select a teacher AND change the conflicting field
    for (const [rowKey, res] of Object.entries(conflictResolutions)) {
      if (!res.selectedTeacherId && !res.isNew) {
        setToastMessage(isRTL ? 'يجب اختيار المنسق الصحيح أولاً' : 'You must select the correct teacher first');
        setToastType('error');
        setToastVisible(true);
        return;
      }
      // Find the conflict to check the conflicting field
      const conflict = importConflicts.find((c: any) => String(c.row) === rowKey);
      if (conflict) {
        const cf = conflict.conflict_field;
        const selected = conflict.candidates.find((c: any) =>
          res.isNew ? c.is_new : c.id === res.selectedTeacherId
        );
        const others = conflict.candidates.filter((c: any) =>
          res.isNew ? !c.is_new : c.id !== res.selectedTeacherId
        );
        for (const other of others) {
          if (cf === 'phone' && res.editedData.phone && other.phone && res.editedData.phone === other.phone) {
            setToastMessage(isRTL ? 'يجب تغيير رقم الهاتف لحل التعارض — الرقم الحالي مستخدم لمنسق آخر' : 'You must change the phone number to resolve the conflict');
            setToastType('error');
            setToastVisible(true);
            return;
          }
          if (cf === 'email' && res.editedData.email && other.email && res.editedData.email.toLowerCase() === other.email.toLowerCase()) {
            setToastMessage(isRTL ? 'يجب تغيير البريد الإلكتروني لحل التعارض — البريد الحالي مستخدم لمنسق آخر' : 'You must change the email to resolve the conflict');
            setToastType('error');
            setToastVisible(true);
            return;
          }
          if (cf === 'eid') {
            const phoneStillSame = res.editedData.phone && other.phone && res.editedData.phone === other.phone;
            const emailStillSame = res.editedData.email && other.email && res.editedData.email.toLowerCase() === other.email.toLowerCase();
            if (phoneStillSame && emailStillSame) {
              setToastMessage(isRTL ? 'يجب تغيير رقم الهاتف أو البريد الإلكتروني لحل التعارض' : 'You must change the phone or email to resolve the conflict');
              setToastType('error');
              setToastVisible(true);
              return;
            }
          }
        }
      }
    }
    setResolving(true);
    try {
      const resolutions = Object.entries(conflictResolutions).map(([rowKey, res]) => {
        const conflict = importConflicts.find((c: any) => String(c.row) === rowKey);
        return {
          teacher_id: res.selectedTeacherId,
          is_new: res.isNew,
          update_data: res.editedData,
          row_data: conflict?.row_data || {},
          specialization_id: importSpecId,
          project_id: importProjId,
          activity_id: importActId,
        };
      });
      const result = await talentTeachersApi.resolveConflicts(resolutions);
      setToastMessage(result.message || (isRTL ? 'تم حل التعارضات بنجاح' : 'Conflicts resolved'));
      setToastType('success');
      setToastVisible(true);
      setShowImportModal(false);
      resetImportWizard();
      await fetchTeachers();
      setCurrentPage(1);
    } catch (err: any) {
      const msg = err?.response?.data?.error || (isRTL ? 'خطأ في معالجة التعارضات' : 'Error resolving conflicts');
      setToastMessage(msg);
      setToastType('error');
      setToastVisible(true);
    } finally {
      setResolving(false);
    }
  };

  // Handle skipping conflicts
  const handleSkipConflicts = () => {
    setShowSkipConfirm(false);
    setShowImportModal(false);
    resetImportWizard();
    setToastMessage(isRTL ? 'تم تخطي المنسقين المتعارضين' : 'Conflicting teachers skipped');
    setToastType('info');
    setToastVisible(true);
  };

  const handleExportExcel = () => {
    const params: any = {};
    if (specializationId) params.specialization = specializationId;
    if (activityId) params.activity = activityId;
    else if (projectId) params.project = projectId;
    talentTeachersApi.exportExcel(params);
  };

  const columns = isRTL
    ? [
        { key: 'name', label: 'اسم المنسق', flex: 1, minWidth: 150 },
        { key: 'school', label: 'المدرسة', flex: 1, minWidth: 160 },
        { key: 'project', label: 'المشروع', flex: 1, minWidth: 160 },
        { key: 'email', label: 'البريد الالكتروني', flex: 1, minWidth: 180 },
        { key: 'phone', label: 'الهاتف', width: 120 },
      ]
    : [
        { key: 'name', label: 'Teacher Name', flex: 1, minWidth: 150 },
        { key: 'school', label: 'School', flex: 1, minWidth: 160 },
        { key: 'project', label: 'Project', flex: 1, minWidth: 160 },
        { key: 'email', label: 'Email', flex: 1, minWidth: 180 },
        { key: 'phone', label: 'Phone', width: 120 },
      ];

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.card, borderColor: colors.cardBorder, overflow: 'visible' as any },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={[styles.header]}>
        <View style={[styles.headerBtns]}>
            {/* Add / Import coordinator — specialization context only, when editable */}
            {canManage && specializationId && (
              <>
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: '#a9211f' }]}
                  onPress={() => setShowAddModal(true)}
                >
                  <Ionicons name="add" size={16} color="#FFFFFF" />
                  <Text style={styles.primaryBtnText}>
                    {isRTL ? 'إضافة منسق' : 'Add Coordinator'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.secondaryBtn, { borderColor: '#CBD5E1' }]}
                  onPress={() => setShowImportModal(true)}
                >
                  <Text style={[styles.secondaryBtnText, { color: colors.textPrimary }]}>
                    {isRTL ? 'استيراد قائمة' : 'Import List'}
                  </Text>
                </TouchableOpacity>
              </>
            )}
            <TouchableOpacity style={[styles.secondaryBtn, { borderColor: '#CBD5E1' }]} onPress={handleExportExcel}>
              <Text style={[styles.secondaryBtnText, { color: colors.textPrimary }]}>
                {isRTL ? 'تصدير Excel' : 'Export Excel'}
              </Text>
            </TouchableOpacity>
          </View>
        <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
          {isRTL ? 'المنسقين' : 'Teachers'}
        </Text>
      </View>

      {/* Table */}
      <ScrollView horizontal contentContainerStyle={{ flexGrow: 1, minWidth: '100%' }}>
        <View style={{ minWidth: 1060, width: '100%' }}>
          {/* Header Row */}
          <View style={[styles.tHeaderRow, { flexDirection: rtlRow, backgroundColor: '#F8FAFC' }]}>
            {columns.map((col) => (
              <View
                key={col.key}
                style={[styles.tHeaderCell, col.flex ? { flex: col.flex, minWidth: col.minWidth } : { width: col.width }]}
              >
                {col.key === 'name' ? (
                  <TouchableOpacity onPress={toggleSort} style={styles.sortHeader}>
                    <Ionicons
                      name={sortOrder === 'asc' ? 'arrow-up' : sortOrder === 'desc' ? 'arrow-down' : 'swap-vertical'}
                      size={14}
                      color={sortOrder ? '#a9211f' : colors.textSecondary}
                    />
                    <Text style={[styles.tHeaderText, { color: colors.textSecondary }]}>{col.label}</Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={[styles.tHeaderText, { color: colors.textSecondary }]}>{col.label}</Text>
                )}
              </View>
            ))}
            <View style={styles.actionCell} />
          </View>

          {/* Data Rows */}
          {loading ? (
            <View style={styles.emptyRow}>
              <ActivityIndicator size="small" color="#a9211f" />
            </View>
          ) : data.results.length > 0 ? (
            data.results.map((teacher) => (
              <View key={teacher.id} style={[styles.dataRow, { flexDirection: rtlRow, borderBottomColor: colors.cardBorder }]}>
                <View style={[styles.cell, { flex: 1, minWidth: 150 }]}>
                  <Text style={[styles.cellText, { color: colors.textPrimary }]}>{teacher.name}</Text>
                </View>
                <View style={[styles.cell, { flex: 1, minWidth: 160 }]}>
                  <Text style={[styles.cellText, { color: colors.textPrimary }]}>{teacher.school}</Text>
                </View>
                <View style={[styles.cell, { flex: 1, minWidth: 160 }]}>
                  <Text style={[styles.cellText, { color: colors.textPrimary }]} numberOfLines={2}>{teacher.projectNames.join('، ')}</Text>
                </View>
                <View style={[styles.cell, { flex: 1, minWidth: 180 }]}>
                  <Text style={[styles.cellText, { color: colors.textPrimary }]}>{teacher.email}</Text>
                </View>
                <View style={[styles.cell, { width: 120 }]}>
                  <Text style={[styles.cellText, { color: colors.textPrimary }]}>{teacher.phone}</Text>
                </View>
                <TouchableOpacity style={styles.actionCell} onPress={() => navigation.navigate('TeacherDetail', { id: teacher.id, entityName })}>
                  <Ionicons name="eye-outline" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            ))
          ) : (
            <View style={styles.emptyRow}>
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                {isRTL ? 'لا توجد بيانات' : 'No data available'}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Pagination */}
      <View style={styles.paginationRow}>
        <View style={styles.paginationButtons}>
          <TouchableOpacity
            onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={[styles.pageBtn, { borderColor: colors.cardBorder }]}
          >
            <Text style={[styles.pageBtnText, { color: currentPage === 1 ? colors.textMuted : colors.textPrimary }]}>
              {isRTL ? 'التالي' : 'Next'}
            </Text>
          </TouchableOpacity>
          <View style={[styles.pageNum, { backgroundColor: '#a9211f', borderColor: '#a9211f' }]}>
            <Text style={[styles.pageNumText, { color: '#FFFFFF' }]}>{currentPage}</Text>
          </View>
          <TouchableOpacity
            onPress={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={[styles.pageBtn, { borderColor: colors.cardBorder }]}
          >
            <Text style={[styles.pageBtnText, { color: currentPage === totalPages ? colors.textMuted : colors.textPrimary }]}>
              {isRTL ? 'السابق' : 'Previous'}
            </Text>
          </TouchableOpacity>
        </View>
        <Text style={[styles.paginationInfo, { color: colors.textSecondary }]}>
          {isRTL
            ? `عرض ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, data.count)} من ${data.count} عنصر`
            : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, data.count)} of ${data.count} items`}
        </Text>
      </View>

      {/* Import Modal — 4-step wizard */}
      <Modal visible={showImportModal} transparent animationType="fade" onRequestClose={() => { setShowImportModal(false); resetImportWizard(); }}>
        <TouchableOpacity style={importStyles.overlay} activeOpacity={1} onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
          <TouchableOpacity activeOpacity={1} style={[importStyles.content, { backgroundColor: isDark ? colors.card : '#FFFFFF' }]}>
            {/* Header */}
            <View style={importStyles.header}>
              <TouchableOpacity onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
                <Ionicons name="close" size={22} color={colors.textPrimary} />
              </TouchableOpacity>
              <Text style={[importStyles.title, { color: colors.textPrimary }]}>
                {isRTL ? 'استيراد قائمة المنسقين' : 'Import Teachers List'}
              </Text>
            </View>

            {importStep === 'pick' ? (
              <>
                {/* Step 1: Download Template */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? colors.border : '#E2E8F0' }]}>
                  <View style={importStyles.stepRow}>
                    <Text style={[importStyles.stepTitle, { color: colors.textPrimary }]}>
                      {isRTL ? '١. تحميل القالب' : '1. Download Template'}
                    </Text>
                    <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}>
                      <Text style={importStyles.stepBadgeText}>1</Text>
                    </View>
                  </View>
                  <Text style={[importStyles.stepDesc, { color: colors.textSecondary }]}>
                    {isRTL
                      ? 'حمّل ملف Excel يحتوي على الأعمدة المطلوبة، ثم املأ بيانات المنسقين'
                      : 'Download an Excel file with required columns, then fill in teacher data'}
                  </Text>
                  <TouchableOpacity style={[importStyles.templateBtn, { borderColor: '#A9211F' }]} onPress={handleDownloadTemplate}>
                    <Ionicons name="download-outline" size={18} color="#A9211F" />
                    <Text style={[importStyles.templateBtnText, { color: '#A9211F' }]}>
                      {isRTL ? 'تحميل القالب' : 'Download Template'}
                    </Text>
                  </TouchableOpacity>
                </View>

                {/* Step 2: Upload File */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? colors.border : '#E2E8F0' }]}>
                  <View style={importStyles.stepRow}>
                    <Text style={[importStyles.stepTitle, { color: colors.textPrimary }]}>
                      {isRTL ? '٢. رفع الملف' : '2. Upload File'}
                    </Text>
                    <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}>
                      <Text style={importStyles.stepBadgeText}>2</Text>
                    </View>
                  </View>
                  <Text style={[importStyles.stepDesc, { color: colors.textSecondary }]}>
                    {isRTL
                      ? 'ارفع ملف Excel المعبأ بالبيانات لاستيراد المنسقين'
                      : 'Upload the filled Excel file to import teachers'}
                  </Text>
                  <TouchableOpacity
                    style={[importStyles.uploadBtn, { backgroundColor: '#A9211F' }]}
                    onPress={handlePickFile}
                  >
                    <Ionicons name="cloud-upload-outline" size={18} color="#FFFFFF" />
                    <Text style={importStyles.uploadBtnText}>
                      {isRTL ? 'اختر ملف Excel' : 'Choose Excel File'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : importStep === 'assign' ? (
              <>
                {/* Step 3: Assignment Questions */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? colors.border : '#E2E8F0' }]}>
                  <View style={importStyles.stepRow}>
                    <Text style={[importStyles.stepTitle, { color: colors.textPrimary }]}>
                      {isRTL ? '٣. تحديد الارتباطات' : '3. Assign To'}
                    </Text>
                    <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}>
                      <Text style={importStyles.stepBadgeText}>3</Text>
                    </View>
                  </View>

                  {/* File name indicator */}
                  <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 8, marginBottom: 12, paddingVertical: 8, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#EFF6FF', borderRadius: 8 }}>
                    <Ionicons name="document-outline" size={18} color="#3B82F6" />
                    <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textPrimary, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {importFile?.name}
                    </Text>
                    <TouchableOpacity onPress={() => { setImportStep('pick'); setImportFile(null); }}>
                      <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#A9211F' }}>
                        {isRTL ? 'تغيير' : 'Change'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* Specialization */}
                  {specializationId ? (
                    <View style={{ marginBottom: 12, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#F8FAFC', borderRadius: 8, borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0' }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>
                        {isRTL ? 'الموهبة' : 'Specialization'}
                      </Text>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                        {assignSpecName || specOptions.find(s => s.id === Number(importSpecId))?.name || '...'}
                      </Text>
                    </View>
                  ) : (
                  <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                      {isRTL ? 'الموهبة' : 'Specialization'} <Text style={{ color: '#DC2626' }}>*</Text>
                    </Text>
                    <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                      <ScrollView horizontal={false} style={{ maxHeight: 200 }}>
                        {specOptions.map((spec) => (
                          <TouchableOpacity
                            key={spec.id}
                            style={{
                              flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                              borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                              backgroundColor: importSpecId === spec.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                            }}
                            onPress={() => onImportSpecChange(spec.id)}
                          >
                            <View style={{
                              width: 18, height: 18, borderRadius: 9, borderWidth: 2,
                              borderColor: importSpecId === spec.id ? '#A9211F' : '#CBD5E1',
                              backgroundColor: importSpecId === spec.id ? '#A9211F' : 'transparent',
                              alignItems: 'center', justifyContent: 'center', marginEnd: 8,
                            }}>
                              {importSpecId === spec.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textPrimary }}>{spec.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  </View>
                  )}

                  {/* Project */}
                  {isAssignMode && projectId ? (
                    <View style={{ marginBottom: 12, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#F8FAFC', borderRadius: 8, borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0' }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>
                        {isRTL ? 'المشروع' : 'Project'}
                      </Text>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                        {assignProjName || projOptions.find(p => p.id === importProjId)?.name || '...'}
                      </Text>
                    </View>
                  ) : projOptions.length > 0 ? (
                    <View style={{ marginBottom: 12 }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                        {isRTL ? 'المشروع (اختياري)' : 'Project (optional)'}
                      </Text>
                      <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                        <ScrollView horizontal={false} style={{ maxHeight: 160 }}>
                          <TouchableOpacity
                            style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importProjId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }}
                            onPress={() => onImportProjChange(undefined)}
                          >
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importProjId ? '#A9211F' : '#CBD5E1', backgroundColor: !importProjId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                              {!importProjId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary }}>{isRTL ? 'بدون مشروع' : 'None'}</Text>
                          </TouchableOpacity>
                          {projOptions.map((proj) => (
                            <TouchableOpacity
                              key={proj.id}
                              style={{
                                flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                                borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                                backgroundColor: importProjId === proj.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                              }}
                              onPress={() => onImportProjChange(proj.id)}
                            >
                              <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importProjId === proj.id ? '#A9211F' : '#CBD5E1', backgroundColor: importProjId === proj.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                                {importProjId === proj.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                              </View>
                              <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textPrimary }}>{proj.name}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>
                    </View>
                  ) : null}

                  {/* Activity — read-only when from activity page, selectable from project page */}
                  {activityId ? (
                    <View style={{ marginBottom: 12, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#F8FAFC', borderRadius: 8, borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0' }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>
                        {isRTL ? 'النشاط' : 'Activity'}
                      </Text>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                        {assignActName || actOptions.find(a => a.id === importActId)?.name || '...'}
                      </Text>
                    </View>
                  ) : actOptions.length > 0 ? (
                    <View style={{ marginBottom: 12 }}>
                      <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                        {isRTL ? 'النشاط (اختياري)' : 'Activity (optional)'}
                      </Text>
                      <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                        <ScrollView horizontal={false} style={{ maxHeight: 160 }}>
                          <TouchableOpacity
                            style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importActId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }}
                            onPress={() => setImportActId(undefined)}
                          >
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importActId ? '#A9211F' : '#CBD5E1', backgroundColor: !importActId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                              {!importActId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary }}>{isRTL ? 'بدون نشاط' : 'None'}</Text>
                          </TouchableOpacity>
                          {actOptions.map((act) => (
                            <TouchableOpacity
                              key={act.id}
                              style={{
                                flexDirection: rtlRow, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
                                borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                                backgroundColor: importActId === act.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent',
                              }}
                              onPress={() => setImportActId(act.id)}
                            >
                              <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importActId === act.id ? '#A9211F' : '#CBD5E1', backgroundColor: importActId === act.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                                {importActId === act.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                              </View>
                              <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textPrimary }}>{act.name}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>
                    </View>
                  ) : null}
                </View>

                {/* Action buttons */}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }}
                    onPress={() => { setImportStep('pick'); setImportFile(null); }}
                  >
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: !importSpecId || importing ? 0.5 : 1 }}
                    onPress={handleImportSubmit}
                    disabled={!importSpecId || importing}
                  >
                    {importing ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'استيراد' : 'Import'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            ) : importStep === 'conflicts' ? (
              <>
                {/* Conflicts Warning Screen */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#FFF7ED', borderColor: '#F59E0B' }]}>
                  <View style={{ alignItems: 'center', marginBottom: 12 }}>
                    <Ionicons name="warning-outline" size={48} color="#F59E0B" />
                  </View>
                  <Text style={{ fontSize: 16, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: 'center', marginBottom: 8 }}>
                    {isRTL
                      ? `وجدنا ${importConflicts.length} منسق لديهم نفس الرقم أو البريد الإلكتروني`
                      : `Found ${importConflicts.length} teachers with duplicate phone or email`}
                  </Text>
                  {importResult && (importResult.created > 0 || importResult.existing > 0) && (
                    <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', marginBottom: 12 }}>
                      {importResult.message}
                    </Text>
                  )}
                  {importConflicts.map((c: any, idx: number) => (
                    <View key={idx} style={{ flexDirection: rtlRow, alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#FFFFFF', borderRadius: 8, marginBottom: 6, borderWidth: 1, borderColor: isDark ? colors.border : '#FDE68A' }}>
                      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#F59E0B', alignItems: 'center', justifyContent: 'center', marginEnd: 10 }}>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{idx + 1}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{c.row_data.name}</Text>
                        <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>
                          {c.conflict_field === 'email'
                            ? (isRTL ? 'البريد الإلكتروني مختلف عن المنسق المسجل' : 'Email differs from registered teacher')
                            : c.conflict_field === 'phone'
                            ? (isRTL ? 'رقم الهاتف مختلف عن المنسق المسجل' : 'Phone differs from registered teacher')
                            : (isRTL ? 'نفس رقم الهوية — يجب تعديل الهاتف أو البريد' : 'Same EID — change phone or email')}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>

                {showSkipConfirm ? (
                  <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#FEF2F2', borderColor: '#DC2626' }]}>
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: 'center', marginBottom: 12 }}>
                      {isRTL ? 'هل أنت متأكد؟ لن يتم رفع هؤلاء المنسقين' : 'Are you sure? These teachers will not be uploaded'}
                    </Text>
                    <View style={{ flexDirection: 'row', gap: 12 }}>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(false)}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={handleSkipConflicts}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تأكيد التخطي' : 'Confirm Skip'}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                    <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(true)}>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'تخطي' : 'Skip'}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center' }} onPress={() => setImportStep('resolve')}>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تعديل واختيار' : 'Edit & Select'}</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </>
            ) : importStep === 'resolve' ? (
              <>
                {/* Conflict Resolution Screen */}
                <View style={importStyles.stepRow}>
                  <Text style={[importStyles.stepTitle, { color: colors.textPrimary, fontSize: 17 }]}>
                    {isRTL ? 'حل التعارضات' : 'Resolve Conflicts'}
                  </Text>
                  <View style={[importStyles.stepBadge, { backgroundColor: '#F59E0B', width: 30, height: 30, borderRadius: 15 }]}>
                    <Text style={[importStyles.stepBadgeText, { fontSize: 14 }]}>{importConflicts.length}</Text>
                  </View>
                </View>

                <ScrollView style={{ flexShrink: 1 }} nestedScrollEnabled>
                  {importConflicts.map((conflict: any, cIdx: number) => {
                    const res = conflictResolutions[conflict.row];
                    if (!res) return null;
                    return (
                      <View key={cIdx} style={{ marginBottom: 20, padding: 16, backgroundColor: isDark ? colors.backgroundAlt : '#FFFBEB', borderRadius: 12, borderWidth: 1, borderColor: isDark ? colors.border : '#FDE68A' }}>
                        {/* Conflict header */}
                        <View style={{ flexDirection: rtlRow, alignItems: 'center', marginBottom: 14, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#FDE68A' }}>
                          <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#F59E0B', alignItems: 'center', justifyContent: 'center', marginEnd: 12 }}>
                            <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{cIdx + 1}</Text>
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={{ fontSize: 16, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{conflict.row_data.name}</Text>
                            {conflict.row_data.eid ? (
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 4, marginTop: 2 }}>
                                <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textSecondary }}>{isRTL ? 'رقم الهوية:' : 'EID:'}</Text>
                                <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{conflict.row_data.eid}</Text>
                              </View>
                            ) : null}
                            <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: '#F59E0B', textAlign: isRTL ? 'right' : 'left', marginTop: 4 }}>
                              {conflict.conflict_field === 'email'
                                ? (isRTL ? 'البريد الإلكتروني مختلف عن المنسق المسجل' : 'Email differs from registered teacher')
                                : conflict.conflict_field === 'phone'
                                ? (isRTL ? 'رقم الهاتف مختلف عن المنسق المسجل' : 'Phone differs from registered teacher')
                                : (isRTL ? 'نفس رقم الهوية — يجب تعديل الهاتف أو البريد' : 'Same EID — change phone or email')}
                            </Text>
                          </View>
                        </View>

                        {/* Candidate selection */}
                        <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }}>
                          {isRTL ? 'اختر المنسق الصحيح:' : 'Select correct teacher:'}
                        </Text>
                        {conflict.candidates.map((cand: any, candIdx: number) => {
                          const candKey = cand.id ?? `new-${candIdx}`;
                          const isSelected = cand.is_new ? res.isNew : (res.selectedTeacherId === cand.id && !res.isNew);
                          return (
                          <TouchableOpacity
                            key={candKey}
                            style={{
                              flexDirection: rtlRow, alignItems: 'center', paddingVertical: 14, paddingHorizontal: 14,
                              borderWidth: 2, borderRadius: 10, marginBottom: 8,
                              borderColor: isSelected ? '#A9211F' : (isDark ? colors.border : '#E2E8F0'),
                              backgroundColor: isSelected ? (isDark ? '#1E3A5F' : '#FEF2F2') : (isDark ? colors.background : '#FFFFFF'),
                            }}
                            onPress={() => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  selectedTeacherId: cand.id,
                                  isNew: !!cand.is_new,
                                  editedData: { name: cand.name, eid: cand.eid || '', email: cand.email || '', phone: cand.phone || '' },
                                  hasEdited: true,
                                },
                              }));
                            }}
                          >
                            <View style={{
                              width: 22, height: 22, borderRadius: 11, borderWidth: 2, marginEnd: 12,
                              borderColor: isSelected ? '#A9211F' : '#CBD5E1',
                              backgroundColor: isSelected ? '#A9211F' : 'transparent',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isSelected && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' }} />}
                            </View>
                            <View style={{ flex: 1 }}>
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 8 }}>
                                <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{cand.name}</Text>
                                {cand.is_new && (
                                  <View style={{ backgroundColor: '#22C55E', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 }}>
                                    <Text style={{ fontSize: 11, fontFamily: FONTS.bold, color: '#FFFFFF' }}>{isRTL ? 'جديد' : 'New'}</Text>
                                  </View>
                                )}
                              </View>
                              {cand.eid ? (
                                <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 4, marginTop: 2 }}>
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary }}>{isRTL ? 'الهوية:' : 'EID:'}</Text>
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.eid}</Text>
                                </View>
                              ) : null}
                              <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Ionicons name="mail-outline" size={13} color={colors.textSecondary} />
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.email || '-'}</Text>
                                </View>
                                <Text style={{ color: colors.textSecondary }}>|</Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                  <Ionicons name="call-outline" size={13} color={colors.textSecondary} />
                                  <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: colors.textSecondary, writingDirection: 'ltr', direction: 'ltr' }}>{cand.phone || '-'}</Text>
                                </View>
                              </View>
                            </View>
                          </TouchableOpacity>
                          );
                        })}

                        {/* Editable fields */}
                        <Text style={{ fontSize: 15, fontFamily: FONTS.bold, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginTop: 14, marginBottom: 8 }}>
                          {isRTL ? 'تعديل البيانات:' : 'Edit details:'}
                        </Text>
                        {/* Name — read only */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'الاسم' : 'Name'}</Text>
                          <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#F8FAFC' }}>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>{res.editedData.name}</Text>
                          </View>
                        </View>
                        {/* EID — read only */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'رقم الهوية' : 'EID'}</Text>
                          <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#F8FAFC' }}>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left' }}>{res.editedData.eid || '-'}</Text>
                          </View>
                        </View>
                        {/* Email — editable */}
                        <View style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Text>
                          <TextInput
                            style={{
                              borderWidth: 1, borderColor: isDark ? colors.border : '#CBD5E1', borderRadius: 8,
                              paddingVertical: 10, paddingHorizontal: 12, fontSize: 14, fontFamily: FONTS.regular,
                              color: colors.textPrimary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left',
                              backgroundColor: isDark ? colors.background : '#FFFFFF',
                            }}
                            value={res.editedData.email}
                            onChangeText={(val) => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  editedData: { ...prev[conflict.row].editedData, email: val },
                                  hasEdited: true,
                                },
                              }));
                            }}
                            keyboardType="email-address"
                            autoCapitalize="none"
                          />
                        </View>
                        {/* Phone — editable */}
                        <View style={{ marginBottom: 4 }}>
                          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 4 }}>{isRTL ? 'الهاتف' : 'Phone'}</Text>
                          <TextInput
                            style={{
                              borderWidth: 1, borderColor: isDark ? colors.border : '#CBD5E1', borderRadius: 8,
                              paddingVertical: 10, paddingHorizontal: 12, fontSize: 14, fontFamily: FONTS.regular,
                              color: colors.textPrimary, writingDirection: 'ltr', direction: 'ltr', textAlign: 'left',
                              backgroundColor: isDark ? colors.background : '#FFFFFF',
                            }}
                            value={res.editedData.phone}
                            onChangeText={(val) => {
                              setConflictResolutions(prev => ({
                                ...prev,
                                [conflict.row]: {
                                  ...prev[conflict.row],
                                  editedData: { ...prev[conflict.row].editedData, phone: val },
                                  hasEdited: true,
                                },
                              }));
                            }}
                            keyboardType="phone-pad"
                          />
                        </View>
                      </View>
                    );
                  })}
                </ScrollView>

                {/* Action buttons */}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 14, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setImportStep('conflicts')}>
                    <Text style={{ fontSize: 15, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, paddingVertical: 14, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: resolving ? 0.5 : 1 }}
                    onPress={handleResolveConflicts}
                    disabled={resolving}
                  >
                    {resolving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ fontSize: 15, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حفظ التعديلات' : 'Save Changes'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            ) : null}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Add Teacher Modal */}
      <Modal visible={showAddModal} transparent animationType="fade" onRequestClose={() => setShowAddModal(false)}>
        <TouchableOpacity style={addModalStyles.overlay} activeOpacity={1} onPress={() => setShowAddModal(false)}>
          <TouchableOpacity style={addModalStyles.contentWrapper} activeOpacity={1} onPress={() => {}}>
            <View style={[addModalStyles.content, { backgroundColor: colors.card }]}>
              <View style={[addModalStyles.header, { flexDirection: rtlRow }]}>
                <Text style={[addModalStyles.title, { color: colors.textPrimary }]}>
                  {isRTL ? 'إضافة منسق جديد' : 'Add New Teacher'}
                </Text>
                <TouchableOpacity onPress={() => setShowAddModal(false)}>
                  <Ionicons name="close" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <ScrollView contentContainerStyle={addModalStyles.body}>
                {/* Select Existing Coordinator Dropdown */}
                <View style={addModalStyles.formField}>
                  <Text style={[addModalStyles.formLabel, { color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left' }]}>
                    {isRTL ? 'اختر منسق موجود أو أضف جديد' : 'Select existing or add new'}
                  </Text>
                  {selectedExistingId ? (
                    <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 8, backgroundColor: '#F0FDF4', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#BBF7D0' }}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontFamily: FONTS.medium, fontSize: 14, color: '#166534', textAlign: isRTL ? 'right' : 'left' }}>
                          {addForm.name}
                        </Text>
                        <Text style={{ fontFamily: FONTS.regular, fontSize: 12, color: '#15803D', textAlign: isRTL ? 'right' : 'left' }}>
                          {isRTL ? 'سيتم إلحاق المنسق الموجود' : 'Will assign existing coordinator'}
                        </Text>
                      </View>
                      <TouchableOpacity onPress={handleClearTeacherSelection}>
                        <Ionicons name="close-circle" size={20} color="#DC2626" />
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <View>
                      <TextInput
                        style={[addModalStyles.formInput, { color: colors.textPrimary, borderColor: colors.border, backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', textAlign: isRTL ? 'right' : 'left' }]}
                        value={existingSearch}
                        onChangeText={setExistingSearch}
                        placeholder={isRTL ? '🔍 ابحث بالاسم أو البريد...' : '🔍 Search by name or email...'}
                        placeholderTextColor={colors.textMuted}
                      />
                      {existingSearch.length >= 2 && (
                        <View style={{ maxHeight: 200, borderWidth: 1, borderColor: colors.border, borderRadius: 8, marginTop: 6, backgroundColor: colors.card }}>
                          <ScrollView nestedScrollEnabled>
                            {existingTeachers
                              .filter(t => t.name.includes(existingSearch) || t.email.includes(existingSearch) || t.phone.includes(existingSearch))
                              .slice(0, 10)
                              .map(t => (
                                <TouchableOpacity key={t.id} onPress={() => handleSelectExistingTeacher(t)} style={{ paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                                  <Text style={{ fontFamily: FONTS.medium, fontSize: 13, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{t.name}</Text>
                                  <Text style={{ fontFamily: FONTS.regular, fontSize: 11, color: colors.textMuted, textAlign: isRTL ? 'right' : 'left' }}>
                                    {t.email || t.phone || ''}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            {existingTeachers.filter(t => t.name.includes(existingSearch) || t.email.includes(existingSearch)).length === 0 && (
                              <Text style={{ padding: 12, fontSize: 12, color: colors.textMuted, textAlign: 'center' }}>
                                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
                              </Text>
                            )}
                          </ScrollView>
                        </View>
                      )}
                    </View>
                  )}
                </View>

                {[
                  { key: 'name' as const, label: isRTL ? 'اسم المنسق' : 'Coordinator Name', placeholder: isRTL ? 'أدخل اسم المنسق' : 'Enter coordinator name' },
                  { key: 'school' as const, label: isRTL ? 'المدرسة' : 'School', placeholder: isRTL ? 'أدخل اسم المدرسة' : 'Enter school name' },
                  { key: 'email' as const, label: isRTL ? 'البريد الالكتروني' : 'Email', placeholder: isRTL ? 'أدخل البريد الالكتروني' : 'Enter email' },
                  { key: 'phone' as const, label: isRTL ? 'الهاتف' : 'Phone', placeholder: isRTL ? 'أدخل رقم الهاتف' : 'Enter phone number' },
                ].map((field) => (
                  <View key={field.key} style={[addModalStyles.formField, field.key === 'school' && { zIndex: 50 }]}>
                    <Text style={[addModalStyles.formLabel, { color: colors.textSecondary, textAlign: isRTL ? 'right' : 'left' }]}>{field.label}</Text>
                    {field.key === 'school' ? (
                      <>
                        <TouchableOpacity
                          style={{
                            borderWidth: 1,
                            borderRadius: 8,
                            paddingHorizontal: 12,
                            paddingVertical: 12,
                            borderColor: colors.border,
                            backgroundColor: selectedExistingId ? '#F3F4F6' : (isDark ? colors.backgroundAlt : '#F9FAFB'),
                            flexDirection: rtlRow,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                          activeOpacity={selectedExistingId ? 1 : 0.7}
                          onPress={() => !selectedExistingId && setShowAddSchoolDropdown(prev => !prev)}
                        >
                          <Text
                            numberOfLines={1}
                            style={{
                              flex: 1,
                              fontFamily: FONTS.regular,
                              fontSize: 13,
                              color: addForm.school ? colors.textPrimary : colors.textMuted,
                              textAlign: isRTL ? 'right' : 'left',
                            }}
                          >
                            {schoolOptions.find(o => o.value === addForm.school)?.label || addForm.school || (isRTL ? 'اختر المدرسة' : 'Select school')}
                          </Text>
                          <Ionicons name="chevron-down" size={16} color={colors.textMuted} />
                        </TouchableOpacity>
                        {showAddSchoolDropdown && !selectedExistingId && (
                          <View style={{
                            maxHeight: 200, marginTop: 6, borderWidth: 1, borderColor: colors.border,
                            borderRadius: 8, backgroundColor: colors.card,
                          }}>
                            <ScrollView nestedScrollEnabled>
                              {schoolOptions.length === 0 && (
                                <Text style={{ padding: 12, fontSize: 12, fontFamily: FONTS.regular, color: colors.textMuted, textAlign: 'center' }}>
                                  {isRTL ? 'لا توجد مدارس متاحة' : 'No schools available'}
                                </Text>
                              )}
                              {schoolOptions.map((opt) => (
                                <TouchableOpacity
                                  key={opt.value}
                                  style={{
                                    paddingVertical: 10, paddingHorizontal: 12,
                                    borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9',
                                    backgroundColor: addForm.school === opt.value ? (isDark ? colors.backgroundAlt : '#F1F5F9') : 'transparent',
                                  }}
                                  onPress={() => {
                                    setAddForm(prev => ({ ...prev, school: opt.value }));
                                    setShowAddSchoolDropdown(false);
                                  }}
                                >
                                  <Text style={{ fontFamily: FONTS.regular, fontSize: 13, color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                                    {opt.label}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          </View>
                        )}
                      </>
                    ) : (
                      <TextInput
                        style={[addModalStyles.formInput, { color: colors.textPrimary, borderColor: colors.border, backgroundColor: selectedExistingId ? '#F3F4F6' : (isDark ? colors.backgroundAlt : '#F9FAFB'), textAlign: isRTL ? 'right' : 'left' }]}
                        placeholder={field.placeholder}
                        placeholderTextColor={colors.textMuted}
                        value={addForm[field.key]}
                        onChangeText={(text) => !selectedExistingId && setAddForm(prev => ({ ...prev, [field.key]: text }))}
                        editable={!selectedExistingId}
                      />
                    )}
                  </View>
                ))}
                <TouchableOpacity
                  style={[addModalStyles.submitButton, addLoading && { opacity: 0.6 }]}
                  onPress={handleAddTeacher}
                  disabled={addLoading}
                >
                  <Text style={addModalStyles.submitButtonText}>
                    {addLoading ? (isRTL ? 'جاري الإضافة...' : 'Adding...') : selectedExistingId ? (isRTL ? 'إلحاق المنسق' : 'Assign Coordinator') : (isRTL ? 'إضافة المنسق' : 'Add Coordinator')}
                  </Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* View Teacher Modal */}
      <Modal visible={!!viewTeacher} transparent animationType="fade" onRequestClose={() => setViewTeacher(null)}>
        <TouchableOpacity style={addModalStyles.overlay} activeOpacity={1} onPress={() => setViewTeacher(null)}>
          <TouchableOpacity activeOpacity={1} onPress={() => {}}>
            <View style={[viewStyles.content, { backgroundColor: colors.card }]}>
              <View style={[addModalStyles.header, { flexDirection: rtlRow }]}>
                <Text style={[addModalStyles.title, { color: colors.textPrimary }]}>
                  {isRTL ? 'بيانات المنسق' : 'Teacher Details'}
                </Text>
                <TouchableOpacity onPress={() => setViewTeacher(null)}>
                  <Ionicons name="close" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              {viewTeacher && (
                <View style={viewStyles.body}>
                  {[
                    { label: isRTL ? 'اسم المنسق' : 'Name', value: viewTeacher.name },
                    { label: isRTL ? 'الموهبة' : 'Specialization', value: viewTeacher.specialization },
                    { label: isRTL ? 'المدرسة' : 'School', value: viewTeacher.school },
                    { label: isRTL ? 'المشروع' : 'Project', value: viewTeacher.projectNames.join('، ') },
                    { label: isRTL ? 'البريد الالكتروني' : 'Email', value: viewTeacher.email },
                    { label: isRTL ? 'الهاتف' : 'Phone', value: viewTeacher.phone },
                  ].map((item) => (
                    <View key={item.label} style={[viewStyles.row, { flexDirection: rtlRow }]}>
                      <Text style={[viewStyles.label, { color: colors.textMuted }]}>{item.label}</Text>
                      <Text style={[viewStyles.value, { color: colors.textPrimary }]}>{item.value || '—'}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      <Modal visible={!!deleteTarget} transparent animationType="fade" onRequestClose={() => setDeleteTarget(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', alignItems: 'center' }}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 8 }}>
              {isRTL ? 'إلغاء إلحاق المنسق' : 'Unassign Teacher'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
              {isRTL ? `هل أنت متأكد من إلغاء إلحاق المنسق "${deleteTarget?.name}"؟ لن يتم حذفه من النظام.` : `Are you sure you want to unassign "${deleteTarget?.name}"? They will not be deleted.`}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setDeleteTarget(null)}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={confirmDeleteTeacher}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'إلغاء الإلحاق' : 'Unassign'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Toast visible={toastVisible} message={toastMessage} type={toastType} onHide={() => setToastVisible(false)} />
      {Platform.OS === 'web' && (
        <input
          ref={fileInputRef as any}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={onFileSelected}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { borderRadius: 8, borderWidth: 1, padding: 12, gap: 12 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  sectionTitle: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right', flex: 1 },
  headerBtns: { flexDirection: 'row', gap: 8 },
  primaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  primaryBtnText: { fontSize: 14, fontFamily: FONTS.regular, color: '#fff' },
  secondaryBtn: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  secondaryBtnText: { fontSize: 14, fontFamily: FONTS.regular },
  tHeaderRow: { borderBottomWidth: 1, borderBottomColor: '#E2E8F0', borderTopLeftRadius: 8, borderTopRightRadius: 8 },
  tHeaderCell: { paddingHorizontal: 8, paddingVertical: 8, justifyContent: 'center', alignItems: 'flex-end' },
  tHeaderText: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right' },
  sortHeader: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dataRow: { borderBottomWidth: StyleSheet.hairlineWidth },
  cell: { paddingHorizontal: 8, paddingVertical: 8, justifyContent: 'center', alignItems: 'flex-end' },
  cellText: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right' },
  actionCell: { width: 44, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  paginationRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 },
  paginationButtons: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pageBtn: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  pageBtnText: { fontSize: 12, fontFamily: FONTS.regular },
  pageNum: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6, minWidth: 34, alignItems: 'center' },
  pageNumText: { fontSize: 12, fontFamily: FONTS.regular },
  paginationInfo: { fontSize: 12, fontFamily: FONTS.regular },
  emptyRow: { padding: 40, alignItems: 'center' as const },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular },
});

const importStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  content: { width: '96%', maxWidth: 780, borderRadius: 12, padding: 24, gap: 16, maxHeight: '98%' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right' },
  section: { borderRadius: 8, borderWidth: 1, padding: 16, gap: 12 },
  stepRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10 },
  stepBadge: { width: 26, height: 26, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  stepBadgeText: { fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' },
  stepTitle: { fontSize: 15, fontFamily: FONTS.medium, textAlign: 'right' },
  stepDesc: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', lineHeight: 20 },
  templateBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  templateBtnText: { fontSize: 14, fontFamily: FONTS.medium },
  uploadBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  uploadBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
});

const addModalStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  // Wrapper carries the width — `content` alone resolves '100%' against a shrink-to-fit parent
  contentWrapper: { width: '100%', maxWidth: 640, maxHeight: '100%' },
  content: { width: '100%', maxHeight: '100%', borderRadius: 12, overflow: 'hidden' },
  header: { justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  title: { fontSize: 18, fontFamily: FONTS.medium },
  body: { padding: 20, gap: 16 },
  formField: { gap: 6 },
  formLabel: { fontSize: 13, fontFamily: FONTS.medium },
  formInput: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 12, fontSize: 14, fontFamily: FONTS.regular },
  submitButton: { backgroundColor: '#a9211f', paddingVertical: 14, borderRadius: 8, alignItems: 'center' },
  submitButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
});

const viewStyles = StyleSheet.create({
  content: { width: '100%', maxWidth: 600, borderRadius: 12, overflow: 'hidden' },
  body: { padding: 20, gap: 4 },
  row: { justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  label: { fontSize: 14, fontFamily: FONTS.medium, minWidth: 140 },
  value: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'left', flex: 1 },
});
